import React from "react";
import { DifferentTypeSection, DifferentTypeCard } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import Link from "next/link";
import { renderRichText } from "@/lib/richText";
import Image from "next/image";

interface DifferentTypeSectionProps {
  blok?: DifferentTypeSection & SbBlokData;
}

type DifferentTypeCardWithSbData = DifferentTypeCard & SbBlokData;

const DifferentTypeSectionComponent: React.FC<DifferentTypeSectionProps> = ({ blok }) => {
  if (!blok) return null;


  const getColumnClasses = (itemCount: number) => {
    switch (itemCount) {
      case 1:
        return "row-cols-1";
      case 2:
        return "row-cols-1 row-cols-lg-2";
      case 3:
        return "row-cols-1 row-cols-md-2 row-cols-xl-3";
      default:
        // For 5 or more items, use 4 columns as maximum
        return "row-cols-1 row-cols-md-2 row-cols-xl-3";
    }
  };

  const itemCount = blok.types?.length || 0;
  const columnClasses = getColumnClasses(itemCount);

  const backgroundClass = blok.bg_style === "dark" ? "bg-light" : "";

  if (blok.type === "white_box") {
    return (
      <div className={`bg-white component-cards-content py-4 py-md-5 ${backgroundClass}`} {...storyblokEditable(blok)}>
        {blok.title && (
          <div className="container">
            <h2 className="col-24 text-center mb-5 text-dark">{blok.title}</h2>
          </div>
        )}
        <div className={`container row ${columnClasses} justify-content-center g-lg-4 mx-auto `}>
          {blok.types?.map((type) => (
            <div key={type._uid} className="col mb-3 text-center" {...storyblokEditable(type as DifferentTypeCardWithSbData)}>
              <div className="card card-lighter overflow-hidden h-100">
                <div className="p-4">
                  {type.icon?.filename && (
                    <Image
                      src={type.icon.filename}
                      alt={type.icon.alt || type.title || ""}
                      width="120"
                      height="120"
                      className="img-fluid mb-2 mx-auto component-cards-content-icon"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                      quality={75}
                    />
                  )}
                  {type.title && (
                    <h3 className="mt-2 text-dark">{type.title}</h3>
                  )}
                  {type.description && (
                    <div className="lead mb-0"
                         dangerouslySetInnerHTML={{ __html: renderRichText(type.description) }} />
                  )}
                  {type.button_text && type.link?.url && (
                    <p className="mt-4 mb-0">
                      <Link className="btn btn-secondary" href={type.link.url}>{type.button_text}</Link>
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (blok.type === "red_icon") {
    return (
      <div className={`container ${backgroundClass}`}>
        <div className="pt-4 pt-lg-5">
          <div>
            {blok.title && (
              <h2 className="mb-4 text-center">{blok.title}</h2>
            )}
            {blok.subtitle && (
              <div className="lead mb-5 text-center"
                   dangerouslySetInnerHTML={{ __html: renderRichText(blok.subtitle) }} />
            )}
            <div className={`row ${columnClasses} g-4 mb-5 align-items-center justify-content-center mx-auto`}>
              {blok.types?.map((type: DifferentTypeCard) => (
                <React.Fragment key={type._uid}>
                  <div className="col-6 col-lg-4 col-xl-auto">
                    {type.icon?.filename && (
                      <Image
                        alt={type.icon.alt || ""}
                        className="img-fluid mb-4"
                        src={type.icon.filename}
                        width={100}
                        height={100}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        loading="lazy"
                        quality={75}
                      />
                    )}
                  </div>
                  <div className="col-18 col-lg-20 col-xl-9">
                    {type.title && <h3>{type.title}</h3>}
                    {type.description && (
                      <div className="lead"
                           dangerouslySetInnerHTML={{ __html: renderRichText(type.description) }} />
                    )}
                    {type.button_text && type.link?.url && (
                      <p className="mt-4 mb-0">
                        <Link className="btn btn-secondary" href={type.link.url}>{type.button_text}</Link>
                      </p>
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (blok.type === "no_box") {
    return (
      <div className={`container py-4 py-md-5 ${backgroundClass}`} {...storyblokEditable(blok)}>
        {blok.title && <h2 className="text-center mb-4">{blok.title}</h2>}
        {blok.subtitle && (
          <div
            className="text-center mb-4"
            dangerouslySetInnerHTML={{ __html: renderRichText(blok.subtitle) }}
          />
        )}
        <div className={`row ${columnClasses} justify-content-center gy-4 gy-md-0`}>
          {blok.types?.map((type: DifferentTypeCard) => (
            <div
              key={type._uid}
              className="col text-center"
            >
              {type.icon?.filename && (
                <Image
                  src={type.icon.filename}
                  alt={type.title || ""}
                  title={type.title || ""}
                  className="mb-3 js-lazy-load"
                  width="70"
                  height="70"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
              )}
              {type.title && <h3 className="mb-2">{type.title}</h3>}
              {type.description && (
                <div className="mb-3"
                     dangerouslySetInnerHTML={{ __html: renderRichText(type.description) }}
                />
              )}
              {type.button_text && type.link?.url && (
                <Link href={type.link.url}>{type.button_text}</Link>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (blok.type === "blue_border") {
    return (
      <section className="different_section_blue_border container py-4 py-md-5" {...storyblokEditable(blok)}>
        <div className="row justify-content-center mb-4">
          <div className="col-lg-20 col-xl-18">
            {blok.title && (
              <h2 className="text-secondary text-center">{blok.title}</h2>
            )}
            {blok.subtitle && (
              <div
                className="text-center mb-0"
                dangerouslySetInnerHTML={{ __html: renderRichText(blok.subtitle) }}
              />
            )}
          </div>
        </div>
        <div className={`row ${columnClasses} justify-content-center`}>
          {blok.types?.map((type: DifferentTypeCard) => (
            <div key={type._uid} className="col-md-12 col-lg-10 col-xl-8">
              <div className="sales-point-w-checklist">
                {type.icon?.filename && (
                  <Image
                    src={type.icon.filename}
                    alt={type.icon.alt || type.title || ""}
                    title={type.title || ""}
                    width={158}
                    height={158}
                    className="js-lazy-load"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    loading="lazy"
                    quality={75}
                  />
                )}
                <div>
                  {type.title && (
                    <h3 className="text-secondary text-center">{type.title}</h3>
                  )}
                  {type.subtitle && <p className="fw-bold text-dark">{type.subtitle}</p>}
                  {type.description && (
                    <div
                      className="list-targets"
                      dangerouslySetInnerHTML={{ __html: renderRichText(type.description) }}
                    />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (blok.type === "blue_bg") {
    return (
      <section className="highlight-section-light py-4 py-md-5">
        <div className="container" {...storyblokEditable(blok)}>
          {blok.title && (
            <h2 className="text-secondary text-center mb-4">{blok.title}</h2>
          )}
          <div className={`row ${columnClasses} gy-4 gy-md-0 text-center`}>
            {blok.types?.map((type: DifferentTypeCard) => (
              <div key={type._uid} className="col">
                {type.icon?.filename && (
                  <Image
                    src={type.icon.filename}
                    alt={type.title || ""}
                    title={type.title || ""}
                    width={190}
                    height={190}
                    className="mb-3 js-lazy-load"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    loading="lazy"
                    quality={75}
                  />
                )}
                {type.title && (
                  <h3 className="text-secondary mb-2">{type.title}</h3>
                )}
                {type.subtitle && <p className="fw-bold text-dark">{type.subtitle}</p>}
                {type.description && (
                  <div className="mb-0"
                       dangerouslySetInnerHTML={{ __html: renderRichText(type.description) }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Default layout
  return (
    <div className={`container different_section_default ${backgroundClass}`} {...storyblokEditable(blok)}>
      <section className="py-4 py-md-5">
        {blok.title && (
          <h2 className="text-secondary text-center mb-lg-4">{blok.title}</h2>
        )}
        {blok.subtitle && (
          <div className="lead text-center mb-5"
               dangerouslySetInnerHTML={{ __html: renderRichText(blok.subtitle) }} />
        )}
        <div className={`row ${columnClasses} g-4`}>
          {blok.types?.map((type) => (
            <div key={type._uid} className="col" {...storyblokEditable(type as DifferentTypeCardWithSbData)}>
              {type.link?.url && !type.button_text ? (
                <Link
                  className="card-penetration-testing card card-lighter h-100"
                  href={type.link.url}
                >
                  <div className="card-body p-lg-4">
                    <div className="panel-text text-center">
                      {type.icon?.filename && (
                        <Image
                          height="125"
                          width="125"
                          alt={type.icon.alt || ""}
                          className="mb-3"
                          src={type.icon.filename}
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          loading="lazy"
                          quality={75}
                        />
                      )}
                      {type.title && (
                        <h3 className="text-secondary text-center mb-lg-3">{type.title}</h3>
                      )}
                      {type.subtitle && <p className="fw-bold text-dark">{type.subtitle}</p>}
                      {type.description && (
                        <div
                          className="lead max-two-thirds mb-0"
                          dangerouslySetInnerHTML={{ __html: renderRichText(type.description) }}
                        />
                      )}
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="card-penetration-testing card card-lighter h-100">
                  <div className="card-body p-lg-4">
                    <div className="panel-text text-center">
                      {type.icon?.filename && (
                        <Image
                          height="125"
                          width="125"
                          alt={type.icon.alt || ""}
                          className="mb-3"
                          src={type.icon.filename}
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                          loading="lazy"
                          quality={75}
                        />
                      )}
                      {type.title && (
                        <h3 className="text-secondary text-center mb-lg-3">{type.title}</h3>
                      )}
                      {type.subtitle && <p className="text-dark">{type.subtitle}</p>}
                      {type.description && (
                        <div
                          className="lead max-two-thirds mb-0"
                          dangerouslySetInnerHTML={{ __html: renderRichText(type.description) }}
                        />
                      )}
                      {type.button_text && type.link?.url && (
                        <p className="mt-4 mb-0">
                          <Link className="btn btn-secondary" href={type.link.url}>{type.button_text}</Link>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DifferentTypeSectionComponent;